import { FC, useEffect, useRef, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Popconfirm,
  Select,
  Switch,
  Tooltip,
  UploadFile,
} from 'antd';
import { AxiosProgressEvent } from 'axios';
import ReactQuill from 'react-quill';
import styled from 'styled-components';
import { useProcContext, ProcBasicFormContext } from '@/entities/proc';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import {
  api,
  AuthorProcPatchRequest,
  useGetAiAssistant,
  useProcPatchDetailsEdit,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { ProgressStatus } from '@/shared/types';
import { ImageInput, Row } from '@/shared/ui';

type Props = {
  onCancel?: () => void;
  onSubmit?: () => void;
};

const toolbar = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ size: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
];

const formats = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  // 'formula'
  // 'image'
  // 'video'
];

export const ProcBasicForm: FC<Props> = ({
  onSubmit: handleSubmit,
  onCancel,
}) => {
  const { accessToken } = useAuth();
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const queryClient = useQueryClient();
  const { theme } = useThemeContext();
  const { proc } = useProcContext();
  const footerRef = useRef<HTMLDivElement>(null);

  const [commentsPopconfirmOpen, setCommentsPopconfirmOpen] = useState(false);

  const [commentsEnabled, setCommentsEnabled] = useState(
    proc?.stepData.administration.commentsEnabled,
  );
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(
    proc?.stepData.administration.aiAssistantEnabled,
  );
  const [showStepContent, setShowStepContent] = useState(
    proc?.stepData.administration.showStepContent,
  );
  const [emailRequired, setEmailRequired] = useState(
    proc?.stepData.administration.emailRequired,
  );

  // image
  const [imageLink, setImageLink] = useState(proc?.imageLink || '');
  const [uploadedImageLink, setUploadedImageLink] = useState(
    proc?.uploadedImageLink || '',
  );
  const [uploadedFileKey, setUploadedFileKey] = useState(proc?.fileKey);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<ProgressStatus>();
  const [uploadProgress, setUploadProgress] = useState(0);

  const { data: assistantData } = useGetAiAssistant(
    { pathParams: { id: activeOrganization?.id || '' } },
    { enabled: Boolean(activeOrganization?.id) },
  );

  const { mutate: updateProc, isLoading: updateProcLoading } =
    useProcPatchDetailsEdit();

  const [form] = Form.useForm();
  const showSubmitButton = Form.useWatch('showSubmitButton', form);

  const onSubmit = async (data: AuthorProcPatchRequest) => {
    // form not edited
    if (
      !form.isFieldsTouched() &&
      imageLink === proc?.imageLink &&
      uploadedImageLink === proc?.uploadedImageLink
    ) {
      handleSubmit?.();
      return;
    }

    const isFile = Boolean(fileList?.length);
    const fileData = isFile && (await uploadImage());

    if (proc?.id) {
      updateProc(
        {
          pathParams: { id: proc.id },
          body: {
            ...data,
            imageLink,
            uploadedFileKey: isFile ? fileData?.fileKey : uploadedFileKey,
          },
        },
        {
          onSuccess() {
            queryClient.invalidateQueries({
              queryKey: ['procs', proc.id],
            });
            handleSubmit?.();
          },
          onError(error: any) {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
          },
        },
      );
    }
  };

  const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
    const { loaded, total } = progressEvent;
    const percentComplete = Math.floor((loaded * 100) / total!);
    setUploadProgress(percentComplete);
  };

  const uploadImage = async () => {
    const formData = new FormData();
    formData.append('file', fileList[0] as any);

    try {
      const { data } = await api.post(
        `/procs/${proc?.id}/file-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress,
        },
      );
      return { fileKey: data.fileKey, value: data.url };
    } catch (error: any) {
      setUploadStatus('exception');
      setIsUploading(false);
      showNotification({
        type: 'error',
        message: 'File upload failed',
        description: error?.stack?.message || 'File upload failed',
      });
    }
  };

  useEffect(() => {
    form.setFieldValue('description', proc?.description);
  }, [proc?.description]);

  return (
    <Form
      form={form}
      name="basic"
      layout="vertical"
      initialValues={{
        remember: true,
        title: proc?.title,
        tags: proc?.tags,
        showSubmitButton: proc?.stepData.administration.showSubmitButton,
        commentsEnabled,
        aiAssistantEnabled,
        emailRequired,
      }}
      onFinish={onSubmit}
      autoComplete="off"
      style={{ marginTop: 10 }}
    >
      <ProcBasicFormContext.Provider
        value={{
          uploadedFileKey,
          setUploadedFileKey,
          imageLink,
          setImageLink,
          uploadedImageLink,
          setUploadedImageLink,
          fileList,
          setFileList,
          uploadStatus,
          setUploadStatus,
          uploadProgress,
          setUploadProgress,
          isUploading,
          setIsUploading,
        }}
      >
        <Col style={{ paddingBottom: footerRef.current?.clientHeight || 80 }}>
          <Form.Item label="Image">
            <ImageInput />
          </Form.Item>

          <Form.Item label="Title" name="title">
            <Input />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            style={{ width: '100%' }}
          >
            <QuillEditor
              modules={{ toolbar }}
              formats={formats}
              placeholder="Add description here..."
              $toolbarBgColor={themeConfig[theme].colors.contentBg}
              $scrollbarThumbColor={themeConfig[theme].colors.disabled}
              $color={themeConfig[theme].colors.text}
            />
          </Form.Item>

          <Form.Item label="Tags" name="tags">
            <Select
              data-testid="tags-input"
              mode="tags"
              suffixIcon={null}
              style={{ width: '100%' }}
              open={false}
              tokenSeparators={[',', ';', ' ']}
            />
          </Form.Item>

          <Row>
            <Form.Item
              label="Live chat label"
              name="commentsLabel"
              initialValue={
                proc?.stepData.administration.commentsLabel || 'Live chat'
              }
              style={{ width: '100%' }}
            >
              <Input />
            </Form.Item>
          </Row>

          <Row>
            <Popconfirm
              title={commentsEnabled ? 'Disable comments?' : 'Enable comments?'}
              description="This action will override the comments settings for all steps."
              open={commentsPopconfirmOpen}
              onConfirm={() => {
                setCommentsPopconfirmOpen(false);
                setCommentsEnabled((oldValue) => !oldValue);
              }}
              onCancel={() =>
                setCommentsPopconfirmOpen(!commentsPopconfirmOpen)
              }
            >
              <Form.Item
                label="Agent chat"
                name="commentsEnabled"
                style={{ width: 'fit-content' }}
              >
                <Switch
                  checked={commentsEnabled}
                  onChange={() => null}
                  onClick={() => setCommentsPopconfirmOpen(true)}
                />
              </Form.Item>
            </Popconfirm>
            <span style={{ marginLeft: -32, marginBottom: -6 }}>
              {commentsEnabled ? 'enabled' : 'disabled'}
            </span>
          </Row>

          <Row>
            <Form.Item
              label="AI assistant"
              name="aiAssistantEnabled"
              tooltip={
                (!assistantData?.id &&
                  'AI assistant is not created for your organization. AI assistant is required in order to enable this feature. You can create it inside organization page.') ||
                (Boolean(assistantData?.aiFreeQueriesCount === 0) &&
                  'You have 0 free queries left. Provide your own OpenAI api key in order to have unlimited queries. You can change the key inside organization page.')
              }
              style={{ width: 'fit-content' }}
            >
              <Switch
                checked={aiAssistantEnabled}
                disabled={!assistantData?.id}
                onClick={() => setAiAssistantEnabled((oldValue) => !oldValue)}
              />
            </Form.Item>
            <span
              style={{
                marginLeft:
                  !assistantData?.id ||
                  Boolean(assistantData?.aiFreeQueriesCount === 0)
                    ? -53
                    : -37,
                marginBottom: -6,
              }}
            >
              {aiAssistantEnabled ? 'enabled' : 'disabled'}
            </span>
          </Row>

          <Row style={{ position: 'relative' }}>
            <Form.Item
              label="Submit button"
              name="showSubmitButton"
              style={{ width: 'fit-content' }}
            >
              <Switch checked={showSubmitButton} />
            </Form.Item>
            <Tooltip title="By enabling submit button, user will have option to submit a proc and you will get a notification after that happens. User will not be able to edit his proc after submitting.">
              <InfoCircleOutlined
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 100,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
            <span style={{ marginLeft: -54, marginBottom: -6 }}>
              {showSubmitButton ? 'enabled' : 'disabled'}
            </span>
          </Row>

          <Row style={{ position: 'relative' }}>
            <Form.Item
              label="Steps content"
              name="showStepContent"
              style={{ width: 'fit-content' }}
            >
              <Switch
                checked={showStepContent}
                onChange={(value) => setShowStepContent(value)}
              />
            </Form.Item>
            <Tooltip title="Display step content in the procedure preview page.">
              <InfoCircleOutlined
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 100,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
            <span style={{ marginLeft: -54, marginBottom: -6 }}>
              {showStepContent ? 'visible' : 'hidden'}
            </span>
          </Row>

          <Row style={{ position: 'relative' }}>
            <Form.Item
              label="Email required"
              name="emailRequired"
              style={{ width: 'fit-content' }}
            >
              <Switch
                checked={emailRequired}
                onChange={(value) => setEmailRequired(value)}
              />
            </Form.Item>
            <Tooltip title="Require email to be filled in the procedure preview page while entering proc with non-authorized user.">
              <InfoCircleOutlined
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 100,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
            <span style={{ marginLeft: -54, marginBottom: -6 }}>
              {emailRequired ? 'enabled' : 'disabled'}
            </span>
          </Row>
        </Col>
        <Col
          ref={footerRef}
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: 'calc(100% - 15px)',
            background: themeConfig[theme].colors.contentBg,
          }}
        >
          <Divider style={{ width: '100%', marginTop: 0 }} />
          <Form.Item
            style={{
              width: '100%',
              paddingRight: 24,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              style={{ marginLeft: 'auto', marginRight: 15 }}
              onClick={() => onCancel && onCancel()}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateProcLoading || isUploading}
            >
              Save and continue
            </Button>
          </Form.Item>
        </Col>
      </ProcBasicFormContext.Provider>
    </Form>
  );
};

const QuillEditor = styled(ReactQuill)<{
  $toolbarBgColor: string;
  $color: string;
  $scrollbarThumbColor: string;
}>`
  .ql-container.ql-snow {
    height: fit-content !important;
  }
  .ql-toolbar.ql-snow {
    background-color: ${({ $toolbarBgColor }) => $toolbarBgColor} !important;
  }
  p,
  span,
  strong {
    color: ${({ $color }) => $color} !important;
  }
  .ql-picker-options {
    background-color: ${({ $toolbarBgColor }) => $toolbarBgColor} !important;
  }
  .ql-editor {
    max-height: 500px;
    min-height: 100px;
    overflow-y: scroll;
    resize: vertical;
    ::-webkit-scrollbar {
      width: 14px;
    }
    ::-webkit-scrollbar-thumb {
      background-color: ${({ $scrollbarThumbColor }) => $scrollbarThumbColor};
      border: 4px solid transparent;
      border-radius: 8px;
      background-clip: padding-box;
    }
  }
`;
